import React, { useCallback, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Clear as ClearIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import PayoutInvoicesTable from './PayoutInvoicesTable';
import { usePayoutInvoices } from '../hooks/usePayoutInvoices';
import type { PayoutInvoiceListItem, PayoutInvoiceStatus } from '@/types/payout';
import { transactionsService } from '@/services';
import { useNotification } from '@/contexts/NotificationContext';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { canPerformAction, ActionKey } from '@/auth/actionMatrix';

const IN_PROGRESS_ONLY: PayoutInvoiceStatus[] = ['IN_PROGRESS'];
/** Одна страница: максимум, который принимает API. */
const CONFIRMATION_PAGE_SIZE = 500;
type PayoutOutAction = 'success' | 'decline';

interface PendingAction {
  row: PayoutInvoiceListItem;
  kind: PayoutOutAction;
}

/** Вкладка «Подтверждение»: только IN_PROGRESS, действия успех / отклонение OUT (query payout_invoice_id). */
const PayoutsOutConfirmation: React.FC = () => {
  const {
    invoices,
    traders,
    error,
    loading,
    filters,
    handleFilterChange,
    handleSearch,
    handleReset,
    handleRefresh,
    loadData,
  } = usePayoutInvoices({ fixedStatuses: IN_PROGRESS_ONLY, perPage: CONFIRMATION_PAGE_SIZE });
  const { showNotification } = useNotification();
  const user = useSelector((state: RootState) => state.auth.user);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const canSuccess = canPerformAction(user, ActionKey.PAYOUTS_OUT_RESOLVE_SUCCESS);
  const canDecline = canPerformAction(user, ActionKey.PAYOUTS_OUT_RESOLVE_DECLINE);

  const runAction = useCallback(
    async (row: PayoutInvoiceListItem, kind: PayoutOutAction) => {
      const payoutInvoiceId = row.id;
      if (kind === 'success' && !canSuccess) {
        showNotification('У вас нет прав для подтверждения', 'error');
        return;
      }
      if (kind === 'decline' && !canDecline) {
        showNotification('У вас нет прав для отклонения', 'error');
        return;
      }
      try {
        setBusyId(row.id);
        if (kind === 'success') {
          await transactionsService.updateOutTransactionToSuccess(payoutInvoiceId, row.initial_amount);
          showNotification('Событие успешной выплаты OUT отправлено', 'success');
        } else {
          await transactionsService.updateOutTransactionToDecline(payoutInvoiceId);
          showNotification('Событие отклонения выплаты OUT отправлено', 'success');
        }
        await loadData(filters);
      } catch {
        showNotification('Ошибка при выполнении операции', 'error');
      } finally {
        setBusyId(null);
        setPendingAction(null);
      }
    },
    [canDecline, canSuccess, filters, loadData, showNotification],
  );

  const requestAction = useCallback(
    (row: PayoutInvoiceListItem, kind: PayoutOutAction) => {
      if (kind === 'success' && !canSuccess) {
        showNotification('У вас нет прав для подтверждения', 'error');
        return;
      }
      if (kind === 'decline' && !canDecline) {
        showNotification('У вас нет прав для отклонения', 'error');
        return;
      }
      setPendingAction({ row, kind });
    },
    [canDecline, canSuccess, showNotification],
  );

  const closeConfirmDialog = useCallback(() => {
    if (busyId) return;
    setPendingAction(null);
  }, [busyId]);

  const confirmPendingAction = useCallback(() => {
    if (!pendingAction) return;
    void runAction(pendingAction.row, pendingAction.kind);
  }, [pendingAction, runAction]);

  const renderRowActions = useCallback(
    (row: PayoutInvoiceListItem) => {
      const busy = busyId === row.id || loading;
      const notOwner = !row.is_claimed_by_me;
      return (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Tooltip title={canSuccess ? '' : 'Недостаточно прав'}>
            <span>
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={busy || !canSuccess || notOwner}
                onClick={() => requestAction(row, 'success')}
              >
                Успех
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={canDecline ? '' : 'Недостаточно прав'}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={busy || !canDecline || notOwner}
                onClick={() => requestAction(row, 'decline')}
              >
                Отклонить
              </Button>
            </span>
          </Tooltip>
        </Stack>
      );
    },
    [busyId, canDecline, canSuccess, loading, requestAction],
  );

  const confirmTitle =
    pendingAction?.kind === 'success'
      ? 'Подтвердить перевод в успех'
      : 'Подтвердить перевод в отмену';
  const confirmText =
    pendingAction?.kind === 'success'
      ? 'Вы точно уверены, что хотите отправить эту OUT-выплату в успех?'
      : 'Вы точно уверены, что хотите отправить эту OUT-выплату в отмену?';
  const confirmButtonText = pendingAction?.kind === 'success' ? 'Да, в успех' : 'Да, в отмену';
  const confirmColor = pendingAction?.kind === 'success' ? 'success' : 'error';
  const confirmBusy = Boolean(pendingAction && busyId === pendingAction.row.id);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Подтверждение
        </Typography>
        <Button variant="contained" onClick={() => handleRefresh()} disabled={loading} startIcon={<RefreshIcon />}>
          Обновить
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={traders}
                value={traders.find((trader) => trader.id === filters.trader_id) ?? null}
                getOptionLabel={(trader) => trader.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_event, trader) => handleFilterChange('trader_id', trader?.id ?? '')}
                renderInput={(params) => (
                  <TextField {...params} label="Трейдер" placeholder="Начните вводить имя" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="ID выплаты"
                value={filters.payout_id}
                onChange={(e) => handleFilterChange('payout_id', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <Button variant="outlined" onClick={handleSearch} fullWidth sx={{ height: '56px' }}>
                Применить
              </Button>
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<ClearIcon />}
                fullWidth
                sx={{ height: '56px' }}
              >
                Сбросить
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <PayoutInvoicesTable
        invoices={invoices}
        loading={loading}
        error={error}
        onClaimChanged={handleRefresh}
        allowClaim
        renderRowActions={renderRowActions}
        maskId={false}
        showConfirmationDuration
      />

      <Dialog open={Boolean(pendingAction)} onClose={closeConfirmDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{confirmText}</DialogContentText>
          {pendingAction && (
            <Stack spacing={0.5}>
              <Typography variant="body2">ID заявки: {pendingAction.row.id}</Typography>
              <Typography variant="body2">Трейдер: {pendingAction.row.trader_name || '-'}</Typography>
              <Typography variant="body2">Сумма: {pendingAction.row.initial_amount}</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} disabled={confirmBusy}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color={confirmColor}
            onClick={confirmPendingAction}
            disabled={confirmBusy}
          >
            {confirmBusy ? 'Отправка...' : confirmButtonText}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayoutsOutConfirmation;
